import { useEffect, useState } from 'react';
import { Err } from '../ui.js';
import { listMailboxes, connectMailbox, setDefaultMailbox, disconnectMailbox, testMailbox, ApiError } from '../api.js';
import type { Mailbox } from '../api.js';

// ─────────────────────────────────────────────────────────────────────────────
// CONNECT YOUR INBOX.
//
// Two buttons and a list, because the operator this is for is not technical and
// the thing being asked of them is already the most intimidating screen in the
// product — a Google consent page asking to send mail on their behalf.
// Everything here exists to make that moment legible: what will be asked, what
// it will and will not be able to do, and what changes afterwards.
//
// The panel says send-only OUT LOUD. People have been trained to fear OAuth
// prompts, correctly, and "we cannot read your mail" is the single fact that
// decides whether they click. It is also true and enforced by the scopes in
// mailbox-core.ts, so saying it is not marketing.
//
// A provider with no client configured is shown as unavailable rather than
// hidden: hiding it makes a deployment look broken in a way nobody can debug,
// and the message names the variable the operator's developer needs to set.
// ─────────────────────────────────────────────────────────────────────────────

export function MailboxPanel() {
  const [rows, setRows] = useState<Mailbox[] | null>(null);
  const [providers, setProviders] = useState<{ google: boolean; microsoft: boolean }>(
    { google: false, microsoft: false });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [proved, setProved] = useState<string | null>(null);

  const load = () => listMailboxes()
    .then((r) => { setRows(r.mailboxes); setProviders(r.providers); })
    .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)));

  useEffect(() => {
    void load();
    // The callback bounces back here with a result in the query string, because
    // the provider redirects a browser and cannot return JSON to the app.
    const outcome = new URLSearchParams(window.location.search).get('mailbox');
    if (outcome && outcome !== 'connected') {
      setError({
        denied: 'You cancelled that connection — nothing changed.',
        failed: 'The provider refused the connection. Try again, and if it repeats the client credentials are wrong.',
        noaddress: 'The provider did not tell us which address it was. Try again and grant the email permission.',
      }[outcome] ?? `Connection failed: ${outcome}`);
    }
    if (outcome) window.history.replaceState({}, '', '/settings');
  }, []);

  async function connect(provider: 'google' | 'microsoft') {
    setBusy(provider); setError(null);
    try {
      const { url } = await connectMailbox(provider);
      // Full navigation, not a popup: popups get blocked, and a blocked popup
      // is indistinguishable from a broken button.
      window.location.href = url;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setBusy(null);
    }
  }

  return (
    <section className="panel">
      <h4>Send from your own inbox</h4>
      <p className="fieldhint">
        Connect Gmail or Outlook and outreach goes out from your address, with
        your domain's own reputation behind it. It lands in your Sent folder and
        replies come back to you. <strong>Send-only — this cannot read your
        mail.</strong>
      </p>

      <Err>{error}</Err>
      {proved && (
        <p className="fieldhint">
          Sent to <strong>{proved}</strong>. Check that inbox — and your Sent
          folder, which is where a real connection puts it. If it arrived, this
          mailbox can carry your outreach.
        </p>
      )}

      {rows?.length === 0 && (
        <p className="fieldhint">
          Nothing connected. Until you connect one, outreach goes out through the
          platform's shared sending domain, which works but shares its reputation
          with every other workspace on it.
        </p>
      )}

      {rows?.map((m) => (
        <div className="row" key={m.id}>
          <span className="rname">
            {m.display_name ? `${m.display_name} <${m.email}>` : m.email}
          </span>
          <span className="meta">{m.provider === 'google' ? 'Gmail' : 'Outlook'}</span>
          <span className={`flag ${m.status === 'connected' ? 'flag-ok' : 'flag-warn'}`}>
            {m.status === 'connected' ? (m.is_default ? 'sending' : 'connected') : 'needs reconnecting'}
          </span>
          <span className="flags">
            {m.status === 'connected' && (
              // Prove it rather than promise it: OAuth can succeed while
              // sending still fails — an admin policy, a scope not really
              // granted — and all of those look like success on this screen
              // until a prospect never receives anything.
              <button className="mini" disabled={busy === m.id} onClick={async () => {
                setBusy(m.id); setError(null); setProved(null);
                try {
                  const r = await testMailbox(m.id);
                  setProved(r.sent_to);
                } catch (e) {
                  setError(e instanceof ApiError ? e.message : String(e));
                } finally { setBusy(null); void load(); }
              }}>{busy === m.id ? 'Sending…' : 'Send me a test'}</button>
            )}
            {m.status === 'connected' && !m.is_default && (
              <button className="mini" onClick={async () => {
                await setDefaultMailbox(m.id); void load();
              }}>Send from this one</button>
            )}
            {m.status !== 'connected' && (
              <button className="mini" onClick={() => void connect(m.provider)}>Reconnect</button>
            )}
            <button className="mini" onClick={async () => {
              await disconnectMailbox(m.id); void load();
            }}>Disconnect</button>
          </span>
          {m.last_error && <p className="fieldhint">{m.last_error}</p>}
        </div>
      ))}

      <div className="actions">
        <button disabled={!providers.google || busy !== null}
          onClick={() => void connect('google')}>
          {busy === 'google' ? 'Opening Google…' : 'Connect Gmail'}
        </button>
        <button disabled={!providers.microsoft || busy !== null}
          onClick={() => void connect('microsoft')}>
          {busy === 'microsoft' ? 'Opening Microsoft…' : 'Connect Outlook'}
        </button>
      </div>

      {(!providers.google || !providers.microsoft) && (
        <p className="fieldhint">
          {!providers.google && !providers.microsoft
            ? 'Neither provider is set up on this deployment yet.'
            : `${!providers.google ? 'Gmail' : 'Outlook'} is not set up on this deployment yet.`}
          {' '}An OAuth client has to be registered once, by whoever runs the
          deployment — the click-by-click is in <code>docs/CONNECT-EMAIL.md</code>,
          and <code>npm run mail:status</code> says what is missing.
        </p>
      )}
    </section>
  );
}
